import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { Mail, CheckCircle2 } from "lucide-react";

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const initialEmail = params.get("email") || "";
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  // Auto-unsubscribe if email is in URL (one-click pattern)
  useEffect(() => {
    if (!initialEmail) return;
    (async () => {
      setBusy(true);
      try {
        await api.post("/auth/unsubscribe", null, { params: { email: initialEmail } });
        setDone(true);
      } catch (e) {
        setErr("Couldn't process this request. Try again below.");
      } finally {
        setBusy(false);
      }
    })();
  }, [initialEmail]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/unsubscribe", null, { params: { email } });
      setDone(true);
    } catch (e) {
      setErr("Couldn't process this request. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout noFooter>
      <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center p-8 md:p-16 bg-linen">
        <div className="w-full max-w-md">
          {done ? (
            <div className="text-center" data-testid="unsub-done">
              <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-mustard border-2 border-black flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-black" strokeWidth={1.5} />
              </div>
              <h1 className="font-serif text-4xl md:text-5xl text-wine mb-3 leading-tight">You're unsubscribed.</h1>
              <p className="text-ink/70 mb-6">
                <strong>{email}</strong> won't receive any more marketing emails from Love, In Full.
                We'll still send security emails (like password resets) since those are requested by you.
              </p>
              <p className="text-xs text-muted">
                Changed your mind? <Link to="/login" className="qld-link">Log in and re-enable from your dashboard.</Link>
              </p>
            </div>
          ) : (
            <>
              <div className="w-12 h-12 mb-5 rounded-full bg-lilac border-2 border-black flex items-center justify-center">
                <Mail className="w-5 h-5 text-black" strokeWidth={1.5} />
              </div>
              <h1 className="font-serif text-4xl md:text-5xl text-wine mb-2">Unsubscribe</h1>
              <p className="text-wine/70 mb-10">
                Enter your email to stop receiving emails from Love, In Full.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-ink mb-1.5">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="qld-input"
                    placeholder="you@example.com"
                    data-testid="unsub-email"
                  />
                </div>
                {err && <p className="text-wine text-sm" data-testid="unsub-error">{err}</p>}
                <button
                  type="submit"
                  disabled={busy}
                  className="qld-btn-primary w-full"
                  data-testid="unsub-submit"
                >
                  {busy ? "Unsubscribing…" : "Unsubscribe me"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
